import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import type { LineCoverageState } from '@his/shared';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { money } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Tooltip } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import type { AuthLine } from '../authorisation';
import { choiceOf, coverageBadge, followUpCandidates, followUpIds, type ExtraChoice } from './coverage-badge';
import { claimBlockOf, claimBlockOfError, claimChipOf, type ClaimBlock } from './claim-gate';

export interface CoverageItemFacts {
  id: string;
  description: string;
  coverageState?: LineCoverageState;
  lockedByApproval?: boolean;
  approvedPct?: number | null;
}

export function CoverageCell({ item }: { item: CoverageItemFacts }) {
  const badge = coverageBadge(item.coverageState ?? 'self_pay');
  const partial = item.coverageState === 'covered' && item.approvedPct !== null && item.approvedPct !== undefined && item.approvedPct < 100;
  return (
    <div className="flex flex-col items-start gap-0.5">
      <span className="inline-flex items-center gap-1.5">
        <Badge variant={badge.variant}>{tr('billing', badge.key)}</Badge>
        {item.lockedByApproval && (
          <Tooltip content={tr('billing', 'nx_locked_by_approval')}>
            <span className="inline-flex text-muted-foreground" role="img" aria-label={tr('billing', 'nx_locked_by_approval')}>
              <Lock className="h-3.5 w-3.5" />
            </span>
          </Tooltip>
        )}
      </span>
      {partial && <span className="text-xs text-warning">{tr('billing', 'nx_x_percent_approved', { pct: item.approvedPct as number })}</span>}
    </div>
  );
}

export function LockedLinesNote({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <Alert className="mb-4">
      <span className="inline-flex items-center gap-1.5">
        <Lock className="h-4 w-4" aria-hidden /> {tr('billing', 'nx_locked_lines_note', { count })}
      </span>
    </Alert>
  );
}

export interface FollowUpScope {
  patientId: string;
  payerId: string;
  encounterId: string | null;
  admissionId: string | null;
}

export function FollowUpPanel({ items, lineOf, scope, channel }: { items: (CoverageItemFacts & { id: string })[]; lineOf: (item: { id: string }) => AuthLine | null; scope: FollowUpScope; channel: 'nphies' | 'manual' | null }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, hasModule } = useAuth();
  const [choices, setChoices] = useState<Record<string, ExtraChoice | undefined>>({});
  const candidates = followUpCandidates(items, (item) => {
    const line = lineOf(item);
    return line !== null && line.state === 'not_sent' && line.needsApproval;
  });
  const chosen = followUpIds(candidates, choices);

  const send = useMutation({
    mutationFn: () =>
      post('/integrations/nphies/pre-auths/request', {
        patientId: scope.patientId,
        payerId: scope.payerId,
        ...(scope.admissionId ? { admissionId: scope.admissionId } : {}),
        ...(scope.encounterId ? { encounterId: scope.encounterId } : {}),
        lines: chosen.flatMap((id) => {
          const line = lineOf({ id });
          return line ? [{ sourceType: line.sourceType, sourceId: line.sourceId }] : [];
        }),
        send: true,
      }),
    onSuccess: () => {
      toast.success(tr('billing', 'nx_follow_up_sent'));
      setChoices({});
      void queryClient.invalidateQueries({ queryKey: ['authorisation'] });
      void queryClient.invalidateQueries({ queryKey: ['visit-insurance'] });
      void queryClient.invalidateQueries({ queryKey: ['invoice'] });
    },
    onError: (caught) => toast.error(tr('billing', 'nx_follow_up_failed'), describeError(caught)),
  });

  if (candidates.length === 0) return null;
  const maySend = channel === 'nphies' && hasModule('nphies') && can('preauth.manage', 'nphies.preauth.send');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('billing', 'nx_added_after_approval')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">{tr('billing', 'nx_added_after_approval_hint')}</p>
        <ul className="space-y-2">
          {candidates.map((item) => {
            const choice = choiceOf(choices, item.id);
            return (
              <li key={item.id} className="rounded-md border p-2">
                <p className="font-medium">{item.description}</p>
                <ChoiceButtons<ExtraChoice>
                  size="sm"
                  inline
                  className="mt-1"
                  aria-label={item.description}
                  value={choice}
                  onChange={(option) => setChoices((current) => ({ ...current, [item.id]: option }))}
                  options={[
                    { value: 'self_pay', label: tr('billing', 'nx_choice_self_pay') },
                    { value: 'follow_up', label: tr('billing', 'nx_choice_follow_up'), disabled: !maySend },
                  ]}
                />
              </li>
            );
          })}
        </ul>
        {maySend && (
          <Button size="sm" loading={send.isPending} disabled={chosen.length === 0} onClick={() => send.mutate()}>
            {tr('billing', 'nx_send_follow_up', { count: chosen.length })}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export interface ClaimInvoice {
  id: string;
  number: string;
  status: string;
  netMinor: number;
  creditMinor: number;
  paidMinor: number;
  everClaimed: boolean;
  insuranceTotalMinor: number;
  encounterId: string | null;
  admissionId: string | null;
  patientId: string;
  payerId: string;
}

export function ClaimAction({ invoice }: { invoice: ClaimInvoice }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [serverBlock, setServerBlock] = useState<ClaimBlock | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const encounter = useQuery({
    queryKey: ['claim-gate-encounter', invoice.encounterId],
    queryFn: () => get<{ status: string }>(`/encounters/${invoice.encounterId}`),
    enabled: Boolean(invoice.encounterId) && !invoice.admissionId && can('encounter.read', 'encounter.read.own'),
    staleTime: 30_000,
    retry: false,
  });
  const admission = useQuery({
    queryKey: ['claim-gate-admission', invoice.admissionId],
    queryFn: () => get<{ status: string }>(`/ipd/admissions/${invoice.admissionId}`),
    enabled: Boolean(invoice.admissionId) && can('admission.read'),
    staleTime: 30_000,
    retry: false,
  });

  const create = useMutation({
    mutationFn: () => post('/insurance/claims', { patientId: invoice.patientId, payerId: invoice.payerId, invoiceIds: [invoice.id], ...(invoice.admissionId ? { admissionId: invoice.admissionId } : {}) }),
    onSuccess: () => {
      toast.success(tr('billing', 'nx_claim_created'));
      setServerBlock(null);
      setFailure(null);
      void queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] });
      void queryClient.invalidateQueries({ queryKey: ['claims'] });
      void queryClient.invalidateQueries({ queryKey: ['visit-insurance'] });
    },
    onError: (caught) => {
      const block = claimBlockOfError(caught);
      setServerBlock(block);
      setFailure(block ? null : describeError(caught));
    },
  });

  if (!can('claim.manage')) return null;

  const local = claimBlockOf({
    status: invoice.status,
    number: invoice.number,
    netMinor: invoice.netMinor,
    creditMinor: invoice.creditMinor,
    paidMinor: invoice.paidMinor,
    everClaimed: invoice.everClaimed,
    hasApproval: invoice.insuranceTotalMinor > 0,
    encounterId: invoice.encounterId,
    admissionId: invoice.admissionId,
    encounterStatus: encounter.data?.status,
    admissionStatus: admission.data?.status,
  });
  const block = local ?? serverBlock;
  const chip = claimChipOf(local, invoice.insuranceTotalMinor > 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          {tr('billing', 'nx_claim')}
          {chip === 'created' && <Badge variant="success">{tr('billing', 'nx_claim_created')}</Badge>}
          {chip === 'auto_pending' && <Badge variant="info">{tr('billing', 'nx_claim_auto_pending')}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <Button size="sm" loading={create.isPending} disabled={Boolean(local)} onClick={() => create.mutate()}>
          {tr('billing', 'nx_create_claim')}
        </Button>
        {block && <p className="text-xs text-muted-foreground">{tr('billing', block.key, { number: block.number ?? invoice.number, due: money(block.dueMinor) })}</p>}
        {!block && <p className="text-xs text-muted-foreground">{tr('billing', 'nx_claim_ready')}</p>}
        {failure && <Alert variant="destructive">{failure}</Alert>}
      </CardContent>
    </Card>
  );
}
